import { Component, Input, OnChanges, SimpleChanges, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService, ManagedUser } from '../services/auth.service';

type Draft = {
  id: number | null;
  username: string;
  displayName: string;
  password: string;
  isAdmin: boolean;
};

function emptyDraft(): Draft {
  return { id: null, username: '', displayName: '', password: '', isAdmin: false };
}

@Component({
    selector: 'app-settings-modal',
    imports: [CommonModule, FormsModule],
    templateUrl: './settings-modal.component.html'
})
export class SettingsModalComponent implements OnChanges {
  private auth = inject(AuthService);

  @Input() open = false;
  @Input() onClose: (() => void) | undefined;

  users = signal<ManagedUser[]>([]);
  loading = signal(false);
  busy = signal(false);
  error = signal<string | null>(null);
  notice = signal<string | null>(null);

  /** null = list view, otherwise the account being created or edited. */
  draft = signal<Draft | null>(null);
  confirmDeleteId = signal<number | null>(null);

  isInternal = this.auth.isInternal;
  currentUser = this.auth.currentUser;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open) {
      this.draft.set(null);
      this.confirmDeleteId.set(null);
      this.error.set(null);
      this.notice.set(null);
      this.reload();
    }
  }

  private reload() {
    this.loading.set(true);
    this.auth.listUsers().subscribe({
      next: users => {
        this.users.set(users);
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Benutzer konnten nicht geladen werden.');
        this.loading.set(false);
      },
    });
  }

  get isEditing() {
    return this.draft() !== null;
  }

  startCreate() {
    this.error.set(null);
    this.notice.set(null);
    this.draft.set(emptyDraft());
  }

  startEdit(user: ManagedUser) {
    this.error.set(null);
    this.notice.set(null);
    this.draft.set({
      id: user.id,
      username: user.username,
      displayName: user.displayName,
      password: '',
      isAdmin: user.isAdmin,
    });
  }

  cancelEdit() {
    this.draft.set(null);
    this.error.set(null);
  }

  /** The first account is always an admin — the backend enforces this too. */
  forcedAdmin(): boolean {
    return this.draft()?.id === null && this.users().length === 0;
  }

  save() {
    const draft = this.draft();
    if (!draft || this.busy()) return;

    this.busy.set(true);
    this.error.set(null);

    const done = (message: string) => {
      this.busy.set(false);
      this.draft.set(null);
      this.notice.set(message);
      this.reload();
      // Our own admin flag may have changed — refresh what the navbar shows.
      this.auth.loadSession().subscribe({ error: () => {} });
    };

    const fail = (err: HttpErrorResponse) => {
      this.busy.set(false);
      this.error.set(err.error?.message ?? 'Speichern fehlgeschlagen.');
    };

    if (draft.id === null) {
      this.auth.createUser({
        username: draft.username.trim(),
        password: draft.password,
        displayName: draft.displayName.trim(),
        isAdmin: draft.isAdmin,
      }).subscribe({
        next: () => done(`Benutzer „${draft.displayName.trim()}“ wurde angelegt.`),
        error: fail,
      });
    } else {
      const changes: { displayName: string; isAdmin: boolean; password?: string } = {
        displayName: draft.displayName.trim(),
        isAdmin: draft.isAdmin,
      };
      // An empty field means "leave the password alone".
      if (draft.password) changes.password = draft.password;

      this.auth.updateUser(draft.id, changes).subscribe({
        next: () => done(
          draft.password
            ? `Benutzer „${draft.displayName.trim()}“ wurde gespeichert, das neue Kennwort gilt sofort.`
            : `Benutzer „${draft.displayName.trim()}“ wurde gespeichert.`,
        ),
        error: fail,
      });
    }
  }

  askDelete(user: ManagedUser) {
    this.error.set(null);
    this.notice.set(null);
    this.confirmDeleteId.set(user.id);
  }

  cancelDelete() {
    this.confirmDeleteId.set(null);
  }

  confirmDelete(user: ManagedUser) {
    if (this.busy()) return;
    this.busy.set(true);
    this.auth.deleteUser(user.id).subscribe({
      next: () => {
        this.busy.set(false);
        this.confirmDeleteId.set(null);
        this.notice.set(`Benutzer „${user.displayName}“ wurde gelöscht.`);
        this.reload();
      },
      error: (err: HttpErrorResponse) => {
        this.busy.set(false);
        this.confirmDeleteId.set(null);
        this.error.set(err.error?.message ?? 'Löschen fehlgeschlagen.');
      },
    });
  }

  close() {
    this.onClose?.();
  }
}
